import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, AlertCircle, Eye, EyeOff, CalendarRange, Clock, Receipt, Check } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore.ts';
import { Button } from '../../components/Shared/Button.tsx';
import { FormField } from '../../components/Shared/FormField.tsx';
import { useTranslation } from '../../i18n';
import facilityFlowLogo from '../../assets/facilityflow-logo.svg';
import facilityFlowLogoWhite from '../../assets/facilityflow-logo-white.svg';

export function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error: authError, clearError, isAuthenticated, isSuperAdmin } = useAuthStore();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Role-aware default landing: SuperAdmin goes to /admin/tenants, others to /.
  const defaultLanding = isSuperAdmin ? '/admin/tenants' : '/';

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
      navigate(from || defaultLanding, { replace: true });
    }
  }, [isAuthenticated, location, navigate, defaultLanding]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    try {
      await login(identifier, password);

      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        // Re-read the freshly-set SuperAdmin flag after login resolves.
        const fresh = useAuthStore.getState().isSuperAdmin;
        navigate(fresh ? '/admin/tenants' : '/');
      }
    } catch {
      setLocalError(t('auth:messages.loginError'));
    }
  };

  const displayError = localError || authError;

  const highlights = [
    { icon: CalendarRange, key: 'auth:login.highlights.planning' },
    { icon: Clock, key: 'auth:login.highlights.timeclock' },
    { icon: Receipt, key: 'auth:login.highlights.invoicing' },
  ];

  return (
    <div className="min-h-screen bg-paper lg:grid lg:grid-cols-[1.1fr_1fr] xl:grid-cols-[1.25fr_1fr]">
      {/* Left: brand panel */}
      <aside
        className="hidden lg:flex relative flex-col justify-between p-10 xl:p-14 text-white overflow-hidden"
        style={{ background: '#00A871' }}
      >
        {/* Subtle white stripe pattern overlay */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.06) 14px 28px)',
          }}
        />

        {/* Brand top */}
        <div className="relative">
          <img src={facilityFlowLogoWhite} alt="FacilityFlow" className="h-14 w-auto" />
        </div>

        {/* Headline + highlights */}
        <div className="relative max-w-md space-y-8">
          <div>
            <div className="font-mono-num text-[10px] uppercase tracking-[0.22em] text-emerald-100/80 mb-3">
              FacilityFlow Suite
            </div>
            <h1 className="text-[40px] xl:text-[48px] font-semibold leading-[1.05] tracking-tight">
              {t('auth:login.tagline')}
            </h1>
          </div>
          <ul className="space-y-3">
            {highlights.map(({ icon: Icon, key }) => (
              <li key={key} className="flex items-start gap-3 text-[14px] text-emerald-50">
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-emerald-100/70" strokeWidth={2} />
                  {t(key)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Foot */}
        <div className="relative font-mono-num text-[10px] uppercase tracking-[0.22em] text-emerald-100/70">
          {`v${__APP_VERSION__}`}
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile brand block */}
          <div className="lg:hidden text-center mb-6">
            <img
              src={facilityFlowLogo}
              alt="FacilityFlow"
              className="h-16 w-auto mx-auto mb-2"
            />
            <p className="text-[12px] text-slate-500 mt-0.5">{t('auth:login.tagline')}</p>
          </div>

          {/* Login Card */}
          <div className="rounded-xl border border-slate-200 bg-white">
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400">
                {t('auth:login.welcome')}
              </div>
              <h2 className="text-[18px] font-semibold tracking-tight text-slate-900 mt-0.5">
                {t('auth:login.title')}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {displayError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5"
                >
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-[12px] text-red-700">{displayError}</p>
                </div>
              )}

              <FormField
                label={t('auth:login.identifier')}
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={t('auth:login.placeholders.identifier')}
                required
                autoComplete="username"
                autoFocus
              />

              <FormField label={t('auth:login.password')} required>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth:login.placeholders.password')}
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-1.5 pr-10 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword
                        ? t('auth:login.hidePassword', { defaultValue: 'Passwort verbergen' })
                        : t('auth:login.showPassword', { defaultValue: 'Passwort anzeigen' })
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" strokeWidth={2} />
                    ) : (
                      <Eye className="w-4 h-4" strokeWidth={2} />
                    )}
                  </button>
                </div>
              </FormField>

              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-medium text-[#00A871] hover:underline"
                >
                  {t('auth:login.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full justify-center mt-2"
                disabled={isLoading}
                leftIcon={
                  isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <LogIn className="w-4 h-4" strokeWidth={2} />
                  )
                }
              >
                {isLoading ? t('auth:login.loggingIn') : t('auth:login.loginButton')}
              </Button>
            </form>
          </div>

          <p className="lg:hidden text-center font-mono-num text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-6">
            {`v${__APP_VERSION__}`}
          </p>
        </div>
      </main>
    </div>
  );
}
